import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchInvoice,
  formatMoney,
  INVOICE_STATUSES,
  INVOICE_TYPES,
  type InvoiceStatus,
} from "@/lib/invoices";

export const Route = createFileRoute("/_authenticated/partner/invoices/$invoiceId")({
  head: () => ({
    meta: [
      { title: "Invoice detail · Landoo Partner" },
      {
        name: "description",
        content: "View invoice details, update payment status and share with the customer.",
      },
    ],
  }),
  errorComponent: () => (
    <DashboardShell title="Invoice" role="Partner" items={PARTNER_NAV}>
      <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
        This invoice could not be loaded.
      </div>
    </DashboardShell>
  ),
  notFoundComponent: () => (
    <DashboardShell title="Invoice" role="Partner" items={PARTNER_NAV}>
      <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
        Invoice not found.
      </div>
    </DashboardShell>
  ),
  component: InvoiceDetailPage,
});

function InvoiceDetailPage() {
  const { invoiceId } = Route.useParams();
  const qc = useQueryClient();
  const { data: inv, isLoading } = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: () => fetchInvoice(invoiceId),
  });

  const setStatus = async (status: InvoiceStatus) => {
    const { error } = await supabase
      .from("partner_invoices")
      .update({ payment_status: status })
      .eq("id", invoiceId);
    if (error) return toast.error(error.message);
    toast.success("Payment status updated");
    qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
    qc.invalidateQueries({ queryKey: ["invoices"] });
  };

  return (
    <DashboardShell title="Invoice" role="Partner" items={PARTNER_NAV}>
      <Link
        to="/partner/invoices"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-brown-dark"
      >
        <ArrowLeft className="h-4 w-4" /> Back to invoices
      </Link>

      {isLoading && <div className="mt-4 text-sm text-muted-foreground">Loading invoice…</div>}
      {!isLoading && !inv && (
        <div className="mt-4 rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Invoice not found.
        </div>
      )}

      {inv && (
        <>
          <div className="mt-3 rounded-2xl border border-border bg-card p-6 shadow-soft">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-lg font-bold text-brown-dark">{inv.invoice_number}</div>
                <div className="text-xs text-muted-foreground">
                  {INVOICE_TYPES.find((t) => t.value === inv.invoice_type)?.label} ·{" "}
                  {inv.invoice_date}
                  {inv.due_date ? ` · due ${inv.due_date}` : ""}
                </div>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                  INVOICE_STATUSES.find((s) => s.value === inv.payment_status)?.cls
                }`}
              >
                {INVOICE_STATUSES.find((s) => s.value === inv.payment_status)?.label}
              </span>
            </div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Block title="From">
                <div className="font-semibold text-brown-dark">{inv.business_name || "—"}</div>
                <div>{inv.business_address}</div>
                <div>{inv.business_phone}</div>
              </Block>
              <Block title="Bill to">
                <div className="font-semibold text-brown-dark">{inv.customer_name}</div>
                <div>{inv.customer_address}</div>
                <div>{inv.customer_mobile}</div>
                <div>{inv.customer_email}</div>
              </Block>
            </div>

            <div className="mt-5 overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2">Qty</th>
                    <th className="px-3 py-2">Rate</th>
                    <th className="px-3 py-2">Tax %</th>
                    <th className="px-3 py-2">Discount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-border">
                    <td className="px-3 py-2">{inv.description || "Service"}</td>
                    <td className="px-3 py-2">{inv.quantity}</td>
                    <td className="px-3 py-2">{formatMoney(inv.rate)}</td>
                    <td className="px-3 py-2">{inv.tax_percent}%</td>
                    <td className="px-3 py-2">{formatMoney(inv.discount)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between rounded-xl bg-brand-soft px-4 py-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-brown-dark">
                Total
              </span>
              <span className="text-lg font-bold text-brown-dark">{formatMoney(inv.total)}</span>
            </div>

            {(inv.notes || inv.terms) && (
              <div className="mt-4 space-y-2 text-xs text-muted-foreground">
                {inv.notes && (
                  <p>
                    <span className="font-semibold text-brown-dark">Notes: </span>
                    {inv.notes}
                  </p>
                )}
                {inv.terms && (
                  <p>
                    <span className="font-semibold text-brown-dark">Terms: </span>
                    {inv.terms}
                  </p>
                )}
              </div>
            )}

            {inv.signature_name && (
              <div className="mt-5 text-right text-xs text-muted-foreground">
                Authorised signatory
                <div className="text-sm font-semibold text-brown-dark">{inv.signature_name}</div>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {INVOICE_STATUSES.map((s) => (
              <button
                key={s.value}
                onClick={() => setStatus(s.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  inv.payment_status === s.value
                    ? s.cls
                    : "bg-secondary/70 text-brown-dark hover:bg-brand-soft"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => toast.info("PDF download will be available in a future release")}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-background px-4 py-2 text-xs font-semibold text-brown-dark"
            >
              <Download className="h-4 w-4" /> Download PDF
            </button>
            <button
              onClick={() => toast.info("Sharing to WhatsApp and email is coming soon")}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-background px-4 py-2 text-xs font-semibold text-brown-dark"
            >
              <Share2 className="h-4 w-4" /> Share
            </button>
          </div>
        </>
      )}
    </DashboardShell>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="text-xs text-muted-foreground">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider">{title}</div>
      {children}
    </div>
  );
}
